import { API_BASE_URL, VAPID_PUBLIC_KEY } from "../environment";

const urlBase64ToUint8Array = (base64String: string) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  const match =
    userAgent.match(/(edg)\/([\d.]+)/i) ||
    userAgent.match(/(opr)\/([\d.]+)/i) ||
    userAgent.match(/(chrome|crios)\/([\d.]+)/i) ||
    userAgent.match(/(firefox|fxios)\/([\d.]+)/i) ||
    userAgent.match(/version\/([\d.]+).*safari/i);

  if (!match) return { browser: "Unknown", version: "Unknown" };

  const key = match[1]?.toLowerCase();
  const version = match[2] || match[1];
  const nameMap: Record<string, string> = {
    edg: "Edge",
    opr: "Opera",
    chrome: "Chrome",
    crios: "Chrome",
    firefox: "Firefox",
    fxios: "Firefox",
  };

  if (key && nameMap[key]) {
    return { browser: nameMap[key], version: match[2] };
  }
  if (/safari/i.test(userAgent)) {
    return { browser: "Safari", version };
  }
  return { browser: "Unknown", version: "Unknown" };
};

const DEVICE_ID_STORAGE_KEY = "pushDeviceId";

/**
 * Browsers don't expose a real device identifier, so we generate one UUID
 * per browser/device on first use and persist it, reusing the same value on
 * every later subscribe call so the backend can recognize this device.
 */
const getDeviceId = (): string => {
  let deviceId = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (!deviceId) {
    deviceId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `device-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
  }
  return deviceId;
};

const getOSInfo = () => {
  const userAgent = navigator.userAgent;
  if (/Windows NT ([\d.]+)/i.test(userAgent)) {
    return { os: "Windows", version: userAgent.match(/Windows NT ([\d.]+)/i)?.[1] || "Unknown" };
  }
  if (/Mac OS X ([\d_]+)/i.test(userAgent)) {
    return { os: "MacOS", version: userAgent.match(/Mac OS X ([\d_]+)/i)?.[1]?.replace(/_/g, ".") || "Unknown" };
  }
  if (/Android ([\d.]+)/i.test(userAgent)) {
    return { os: "Android", version: userAgent.match(/Android ([\d.]+)/i)?.[1] || "Unknown" };
  }
  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    const version = userAgent.match(/OS ([\d_]+)/i)?.[1]?.replace(/_/g, ".") || "Unknown";
    return { os: "iOS", version };
  }
  if (userAgent.indexOf("X11") !== -1) return { os: "UNIX", version: "Unknown" };
  if (userAgent.indexOf("Linux") !== -1) return { os: "Linux", version: "Unknown" };
  return { os: "Unknown", version: "Unknown" };
};

export const isPushNotificationSupported = () =>
  window.isSecureContext &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export const getExistingPushSubscription = async () => {
  if (!isPushNotificationSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
};

/**
 * Asks the browser for notification permission. Must be called directly from
 * a user gesture (e.g. a click handler) — do not await anything else first,
 * or the browser may silently ignore the request.
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!isPushNotificationSupported()) return "denied";
  // We intentionally do not short-circuit here if Notification.permission is already granted.
  // By forcing Notification.requestPermission(), we allow Chrome to natively delegate
  // the permission prompt to an installed WebAPK if its native OS permission is still default.
  return Notification.requestPermission();
};

/**
 * Creates (or reuses) a PushManager subscription and registers it with the
 * backend. Assumes notification permission has already been granted.
 */
export const registerPushSubscription = async (
  tenantId: number,
  userId: number
): Promise<boolean> => {
  if (!isPushNotificationSupported() || Notification.permission !== "granted") {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      const convertedVapidKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey,
      });
    }

    const subscriptionData = JSON.parse(JSON.stringify(subscription));
    const { browser, version: browserVersion } = getBrowserInfo();
    const { os, version: osVersion } = getOSInfo();

    const payload = {
      tenant_id: tenantId,
      user_id: userId,
      device_id: getDeviceId(),
      endpoint: subscriptionData.endpoint,
      p256dh: subscriptionData.keys.p256dh,
      auth: subscriptionData.keys.auth,
      device_type: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? "mobile" : "desktop",
      browser,
      browser_version: browserVersion,
      os,
      os_version: osVersion,
      device_name: navigator.platform || "Unknown",
      is_active: true,
      created_by: userId,
      updated_by: userId,
    };

    const authCred = localStorage.getItem("authCred");
    const response = await fetch(`${API_BASE_URL}/push-subscription/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(authCred ? { Authorization: `Basic ${authCred}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    // A 400 for a duplicate endpoint means this device is already subscribed.
    return response.ok || response.status === 400;
  } catch (error) {
    console.error("Failed to register push subscription:", error);
    return false;
  }
};

/**
 * Convenience wrapper: requests permission and, if granted, registers the
 * subscription with the backend in one call.
 */
export const subscribeToPushNotifications = async (
  tenantId: number,
  userId: number
): Promise<boolean> => {
  const permission = await requestNotificationPermission();
  if (permission !== "granted") return false;
  return registerPushSubscription(tenantId, userId);
};
